import React, { useState, useEffect, useRef, Component } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  MapPin, Star, Shield, X, ArrowRight,
  Zap, Lock, Activity, Target, ChevronRight, Layers
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { INDUSTRY_TYPES, CLAIMABLE_INDUSTRY_TYPES, getIndustryType } from "@/constants/industries";
import { valueToSlug } from "@/pages/ZipLandingPage";
import SEOHead from "@/components/SEOHead";
import RotatingBanners from "@/components/RotatingBanners";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const FIPS_TO_STATE = {
  "01":"AL","02":"AK","04":"AZ","05":"AR","06":"CA","08":"CO","09":"CT",
  "10":"DE","11":"DC","12":"FL","13":"GA","15":"HI","16":"ID","17":"IL",
  "18":"IN","19":"IA","20":"KS","21":"KY","22":"LA","23":"ME","24":"MD",
  "25":"MA","26":"MI","27":"MN","28":"MS","29":"MO","30":"MT","31":"NE",
  "32":"NV","33":"NH","34":"NJ","35":"NM","36":"NY","37":"NC","38":"ND",
  "39":"OH","40":"OK","41":"OR","42":"PA","44":"RI","45":"SC","46":"SD",
  "47":"TN","48":"TX","49":"UT","50":"VT","51":"VA","53":"WA","54":"WV",
  "55":"WI","56":"WY"
};

const STATE_NAMES = {
  "AL":"Alabama","AK":"Alaska","AZ":"Arizona","AR":"Arkansas","CA":"California",
  "CO":"Colorado","CT":"Connecticut","DE":"Delaware","DC":"D.C.","FL":"Florida",
  "GA":"Georgia","HI":"Hawaii","ID":"Idaho","IL":"Illinois","IN":"Indiana",
  "IA":"Iowa","KS":"Kansas","KY":"Kentucky","LA":"Louisiana","ME":"Maine",
  "MD":"Maryland","MA":"Massachusetts","MI":"Michigan","MN":"Minnesota",
  "MS":"Mississippi","MO":"Missouri","MT":"Montana","NE":"Nebraska","NV":"Nevada",
  "NH":"New Hampshire","NJ":"New Jersey","NM":"New Mexico","NY":"New York",
  "NC":"North Carolina","ND":"North Dakota","OH":"Ohio","OK":"Oklahoma",
  "OR":"Oregon","PA":"Pennsylvania","RI":"Rhode Island","SC":"South Carolina",
  "SD":"South Dakota","TN":"Tennessee","TX":"Texas","UT":"Utah","VT":"Vermont",
  "VA":"Virginia","WA":"Washington","WV":"West Virginia","WI":"Wisconsin","WY":"Wyoming"
};

// ─── Per-industry color tiers for map fills ─────────────────────────────────
const INDUSTRY_MAP_COLORS = {
  tax_pro:            { low: "#0d4a2e", mid: "#15803d", bright: "#2ECC71", hover: "#4ade80" },
  legal_help:         { low: "#1e1b6b", mid: "#3730a3", bright: "#6366f1", hover: "#818cf8" },
  debt_relief:        { low: "#5a0e1e", mid: "#9f1239", bright: "#f43f5e", hover: "#fb7185" },
  insurance:          { low: "#0c3555", mid: "#0369a1", bright: "#38bdf8", hover: "#7dd3fc" },
  retirement_planner: { low: "#2e1b5e", mid: "#5b21b6", bright: "#a78bfa", hover: "#c4b5fd" },
  financial_advisor:  { low: "#073248", mid: "#0e7490", bright: "#06b6d4", hover: "#67e8f9" },
  personal_loan:      { low: "#422006", mid: "#92400e", bright: "#facc15", hover: "#fde047" },
  real_estate:        { low: "#1a2e05", mid: "#365314", bright: "#84cc16", hover: "#bef264" },
  mortgage_broker:    { low: "#0a2e2e", mid: "#115e59", bright: "#14b8a6", hover: "#5eead4" },
  home_improvement:   { low: "#431407", mid: "#9a3412", bright: "#fb923c", hover: "#fdba74" },
  auto_dealer:        { low: "#431407", mid: "#c2410c", bright: "#f97316", hover: "#fb923c" },
  job_posting:        { low: "#3b0764", mid: "#7e22ce", bright: "#e879f9", hover: "#f0abfc" },
};

// Default multi-color heat scale when "all" selected
const DEFAULT_TIERS = {
  low: "#1a3a6c", mid: "#1d4ed8", bright: "#7c3aed", hover: "#f59e0b"
};

// ─── Map state fill — now industry-aware ─────────────────────────────────────
function getStateFill(count, isSelected, isHovered, filterType) {
  if (isSelected) return "#2ECC71";

  const tiers = filterType !== "all" && INDUSTRY_MAP_COLORS[filterType]
    ? INDUSTRY_MAP_COLORS[filterType]
    : DEFAULT_TIERS;

  if (isHovered) {
    return count === 0
      ? (filterType !== "all" ? tiers.low : "#1e3e72")
      : tiers.hover;
  }

  if (count === 0) return "#080e1a";
  if (count <= 3)  return tiers.low;
  if (count <= 10) return tiers.mid;
  return tiers.bright;
}

// ─── Animated counter hook ───────────────────────────────────────────────────
function useCountUp(target, duration = 1400) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!target) return;
    let current = 0;
    const steps = 60;
    const increment = target / steps;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(current));
    }, duration / steps);
    return () => clearInterval(timer);
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps
  return count;
}

// ─── Track banner click ───────────────────────────────────────────────────────
async function trackAndOpen(banner) {
  if (!banner.cta_url) return;
  try {
    const res = await axios.post(`${API}/marketplace/banners/${banner.id}/click`);
    window.open(res.data.cta_url, "_blank", "noopener,noreferrer");
  } catch {
    window.open(banner.cta_url, "_blank", "noopener,noreferrer");
  }
}

// ─── Rank Medal ───────────────────────────────────────────────────────────────
function RankMedal({ rank }) {
  const style =
    rank === 1 ? { color: "#FFD700", bg: "rgba(255,215,0,0.18)", border: "rgba(255,215,0,0.5)", glow: "0 0 12px rgba(255,215,0,0.4)" } :
    rank === 2 ? { color: "#CBD5E1", bg: "rgba(203,213,225,0.14)", border: "rgba(203,213,225,0.35)", glow: "none" } :
    rank === 3 ? { color: "#CD7F32", bg: "rgba(205,127,50,0.18)", border: "rgba(205,127,50,0.4)", glow: "0 0 10px rgba(205,127,50,0.3)" } :
                 { color: "#475569", bg: "rgba(71,85,105,0.1)",   border: "rgba(71,85,105,0.2)",   glow: "none" };
  return (
    <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0"
      style={{ color: style.color, background: style.bg, border: `1.5px solid ${style.border}`, boxShadow: style.glow }}>
      {rank}
    </div>
  );
}

// ─── Claimed Card ─────────────────────────────────────────────────────────────
function ClaimedCard({ listing }) {
  const industry = getIndustryType(listing.advertiser_type || "tax_pro");
  const IconComp = industry.icon;
  const slug = valueToSlug(listing.advertiser_type || "tax_pro");
  return (
    <Link
      to={`/find/${slug}/${listing.zip_code}`}
      className="claimed-card-hover group block relative overflow-hidden rounded-xl p-4"
      style={{
        background: "rgba(10, 15, 25, 0.92)",
        border: `1px solid ${industry.color}22`,
        boxShadow: `0 2px 20px rgba(0,0,0,0.4), 0 0 0 0 ${industry.color}00`,
        transition: "all 0.25s ease"
      }}
      data-testid={`claimed-card-${listing.id}`}
    >
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl pointer-events-none"
        style={{ background: `radial-gradient(circle at 50% 0%, ${industry.color}12 0%, transparent 70%)`, border: `1px solid ${industry.color}35` }} />
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2 py-0.5 rounded-full"
          style={{ background: `${industry.color}18`, color: industry.color, border: `1px solid ${industry.color}25` }}>
          <IconComp size={10} /> {industry.label}
        </span>
        <span className="ml-auto text-xs text-rose-400 font-bold flex items-center gap-1">
          <Lock size={9} /> CLAIMED
        </span>
      </div>
      <div className="font-black text-2xl text-white group-hover:transition-colors duration-200"
        style={{ transition: "color 0.2s" }}
        onMouseEnter={e => e.currentTarget.style.color = industry.color}
        onMouseLeave={e => e.currentTarget.style.color = "white"}>
        {listing.zip_code}
      </div>
      <div className="text-slate-400 text-sm">{listing.city}, {listing.state}</div>
      <div className="text-slate-300 text-sm font-semibold truncate mt-0.5">{listing.business_name}</div>
      <div className="mt-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity text-xs font-semibold"
        style={{ color: industry.color }}>
        View Profile <ChevronRight size={10} />
      </div>
    </Link>
  );
}

// ─── Map Mode Badge ───────────────────────────────────────────────────────────
function MapModeBadge({ filterType }) {
  if (filterType === "all") return null;
  const ind = INDUSTRY_TYPES.find(i => i.value === filterType);
  if (!ind) return null;
  const IconComp = ind.icon;
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold"
      style={{
        background: `${ind.color}18`,
        border: `1px solid ${ind.color}40`,
        color: ind.color,
        boxShadow: `0 0 12px ${ind.color}30`
      }}>
      <IconComp size={11} />
      {ind.label} Mode
      <div className="w-1.5 h-1.5 rounded-full" style={{ background: ind.color, animation: "livePulse 1.5s infinite" }} />
    </div>
  );
}

// ─── Error Boundary ──────────────────────────────────────────────────────────
class MapErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center" style={{ background: "#060B14" }}>
          <div className="text-center px-6">
            <div className="text-[#2ECC71] text-5xl font-black mb-4">NDR</div>
            <h2 className="text-white text-2xl font-bold mb-2">Map Temporarily Unavailable</h2>
            <p className="text-slate-400 mb-6">We hit a snag loading this view. Try refreshing or selecting a different filter.</p>
            <button onClick={() => window.location.reload()}
              className="px-6 py-3 rounded-xl font-bold text-white"
              style={{ background: "linear-gradient(135deg, #2ECC71, #16a34a)" }}>
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}


export default function USMapPage() {
  return <MapErrorBoundary><USMap /></MapErrorBoundary>;
}

function USMap() {
  const [searchParams] = useSearchParams();
  const initialState = searchParams.get("state") || "all";
  const [mapStats, setMapStats] = useState({ states: {}, total_claimed: 0, by_type: {} });
  const [selectedState, setSelectedState] = useState(initialState !== "all" ? initialState : null);
  const [hoveredState, setHoveredState] = useState(null);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: "", color: "#2ECC71" });
  const [filterType, setFilterType] = useState("all");
  const [filterState, setFilterState] = useState(initialState);
  const [listings, setListings] = useState([]);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const marketplaceRef = useRef(null);

  const totalClaimed = mapStats.total_claimed || 0;
  // Use per-industry state counts when a specific industry is selected
  const stateCounts = filterType !== "all"
    ? (mapStats.states_by_type?.[filterType] || {})
    : (mapStats.states || {});
  const animatedTotal = useCountUp(totalClaimed);

  // Derive active industry for current filter
  const activeIndustry = INDUSTRY_TYPES.find(i => i.value === filterType);
  const activeTiers = filterType !== "all" && INDUSTRY_MAP_COLORS[filterType]
    ? INDUSTRY_MAP_COLORS[filterType]
    : DEFAULT_TIERS;

  useEffect(() => {
    axios.get(`${API}/map/stats`).then(r => setMapStats(r.data)).catch(() => {});
    if (initialState !== "all") {
      setTimeout(() => marketplaceRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 800);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadMarketplace(); }, [filterType, filterState]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMarketplace = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType !== "all") params.type = filterType;
      if (filterState !== "all") params.state = filterState;
      const [listRes, bannerRes] = await Promise.all([
        axios.get(`${API}/marketplace/listings`, { params }),
        axios.get(`${API}/marketplace/banners`, { params }),
      ]);
      setListings(listRes.data.listings || []);
      setBanners(bannerRes.data.banners || []);
    } catch {}
    setLoading(false);
  };

  const handleStateClick = (stateCode) => {
    const next = stateCode === selectedState ? null : stateCode;
    setSelectedState(next);
    setFilterState(next || "all");
    if (next && marketplaceRef.current) {
      setTimeout(() => marketplaceRef.current.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  };

  const activeStates = Object.entries(stateCounts)
    .filter(([, count]) => count > 0)
    .sort(([, a], [, b]) => b - a)
    .map(([code]) => code);

  const topStates = Object.entries(stateCounts)
    .filter(([, c]) => c > 0)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 7);

  // Density legend for current filter mode
  const legendItems = filterType !== "all" && activeIndustry
    ? [
        { color: "#080e1a", label: "None" },
        { color: activeTiers.low, label: "1–3" },
        { color: activeTiers.mid, label: "4–10" },
        { color: activeTiers.bright, label: "11+" },
        { color: "#2ECC71", label: "Selected" },
      ]
    : [
        { color: "#080e1a", label: "None" },
        { color: "#1a3a6c", label: "1–3" },
        { color: "#1d4ed8", label: "4–10" },
        { color: "#7c3aed", label: "11+" },
        { color: "#2ECC71", label: "Selected" },
      ];

  return (
    <div className="min-h-screen" style={{ background: "#060B14" }}>
      <SEOHead
        title="US Advertiser Territory Map — Claimed & Available ZIP Codes"
        description="Explore the interactive US territory map showing which ZIP codes are claimed."
        canonical="/map"
        jsonLd={{
          "@type": "WebPage",
          "name": "NextDayRefund.com US Territory Map",
          "description": `Interactive map showing available and claimed ZIP code advertising territories for ${CLAIMABLE_INDUSTRY_TYPES.length} local service industries.`
        }}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Territory Map", url: "/map" },
        ]}
      />
      <Navbar />

      {/* ── Command Center Hero ────────────────────────────────────────────── */}
      <section className="relative overflow-hidden" style={{ borderBottom: "1px solid rgba(46,204,113,0.12)" }}>
        <div className="absolute inset-0 map-intel-grid pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(46,204,113,0.07) 0%, transparent 70%)"
        }} />

        <div className="section-container py-10 relative">
          <div className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 rounded-full"
            style={{ background: "rgba(46,204,113,0.1)", border: "1px solid rgba(46,204,113,0.22)" }}>
            <div className="w-2 h-2 rounded-full bg-[#2ECC71]"
              style={{ animation: "livePulse 1.8s ease-in-out infinite" }} />
            <span className="text-[#2ECC71] text-xs font-bold tracking-widest uppercase">Live Territory Intelligence</span>
          </div>

          <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8">
            <div className="map-card-animate">
              <h1 className="text-5xl sm:text-6xl font-black text-white mb-3 leading-[1.05]">
                US Territory<br />
                <span style={{
                  background: "linear-gradient(135deg, #2ECC71 0%, #06b6d4 100%)",
                  WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text"
                }}>Command Center</span>
              </h1>
              <p className="text-slate-400 max-w-xl text-base leading-relaxed">
                Each industry has its own color. Filter by industry to see the map transform — exclusive territories.
              </p>
            </div>

            <div className="flex items-stretch gap-3 flex-wrap map-stat-animate">
              <div className="flex flex-col items-center justify-center px-6 py-4 rounded-2xl map-glow-breathe"
                style={{ background: "rgba(46,204,113,0.07)", border: "1px solid rgba(46,204,113,0.22)" }}>
                <div className="text-4xl font-black leading-none" style={{
                  background: "linear-gradient(135deg, #2ECC71, #06b6d4)",
                  WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text"
                }}>{animatedTotal}</div>
                <div className="text-slate-400 text-xs mt-1.5 uppercase tracking-widest font-semibold">Territories Claimed</div>
              </div>
              <div className="flex flex-col items-center justify-center px-5 py-4 rounded-2xl"
                style={{ background: "rgba(14,22,38,0.85)", border: "1px solid rgba(255,255,255,0.08)" }}>
                <div className="text-3xl font-black text-white leading-none">{activeStates.length}</div>
                <div className="text-slate-500 text-xs mt-1.5 uppercase tracking-widest font-semibold">Active States</div>
              </div>
              <div className="flex flex-col items-center justify-center px-5 py-4 rounded-2xl"
                style={{ background: "rgba(14,22,38,0.85)", border: "1px solid rgba(255,255,255,0.08)" }}>
                <div className="text-3xl font-black leading-none" style={{
                  background: "linear-gradient(135deg, #f43f5e, #a78bfa, #38bdf8, #2ECC71)",
                  WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text"
                }}>{CLAIMABLE_INDUSTRY_TYPES.length}</div>
                <div className="text-slate-500 text-xs mt-1.5 uppercase tracking-widest font-semibold">Industries</div>
              </div>
              <div className="flex flex-col justify-center gap-2">
                <Link to="/claim"
                  className="flex items-center gap-2 font-black px-5 py-3 rounded-xl text-sm whitespace-nowrap transition-all duration-200 hover:scale-105 hover:brightness-110"
                  style={{
                    background: "linear-gradient(135deg, #2ECC71 0%, #16a34a 100%)",
                    color: "white",
                    boxShadow: "0 4px 24px rgba(46,204,113,0.35)"
                  }}
                  data-testid="header-claim-cta">
                  <Zap size={15} /> Claim Your ZIP
                </Link>
                <div className="text-center text-slate-500 text-xs font-medium">$150 · Any ZIP · Any Industry</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Active States Quick Nav ──────────────────────────────────────────── */}
      {activeStates.length > 0 && (
        <section style={{ background: "rgba(8,13,22,0.98)", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
          <div className="section-container py-3">
            <div className="flex items-center gap-3 overflow-x-auto scrollbar-hide">
              <span className="text-slate-500 text-xs font-bold uppercase tracking-widest whitespace-nowrap flex-shrink-0 flex items-center gap-1.5">
                <Activity size={11} className="text-[#2ECC71]" /> Hot Zones:
              </span>
              {filterState !== "all" && (
                <button
                  onClick={() => { setSelectedState(null); setFilterState("all"); }}
                  className="flex items-center gap-1 text-xs text-slate-400 hover:text-white px-2 py-1 rounded-full flex-shrink-0 transition-colors"
                  style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <X size={10} /> Clear
                </button>
              )}
              {activeStates.map(code => {
                const isActive = filterState === code;
                return (
                  <button
                    key={code}
                    onClick={() => handleStateClick(code)}
                    data-testid={`state-nav-${code}`}
                    className="flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold transition-all duration-200 flex-shrink-0"
                    style={isActive ? {
                      background: "linear-gradient(135deg, #2ECC71, #16a34a)",
                      color: "white",
                      boxShadow: "0 2px 14px rgba(46,204,113,0.4)"
                    } : {
                      background: "rgba(30,42,62,0.8)",
                      color: "#94a3b8",
                      border: "1px solid rgba(255,255,255,0.07)"
                    }}>
                    <MapPin size={9} />
                    {STATE_NAMES[code] || code}
                    <span className="font-black opacity-70">{stateCounts[code]}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── Industry Filter — Full Color Palette ────────────────────────────── */}
      <section style={{ background: "rgba(8,12,22,0.99)", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
        <div className="section-container py-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-slate-500 text-xs font-bold uppercase tracking-widest flex items-center gap-2">
              <Layers size={11} /> Select Industry Mode — Each color transforms the map
            </p>
            {filterType !== "all" && (
              <button onClick={() => setFilterType("all")}
                className="text-xs text-slate-500 hover:text-white flex items-center gap-1 transition-colors">
                <X size={10} /> Reset
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {INDUSTRY_TYPES.map(ind => {
              const IconComp = ind.icon;
              const isActive = filterType === ind.value;
              const tiers = ind.value !== "all" && INDUSTRY_MAP_COLORS[ind.value]
                ? INDUSTRY_MAP_COLORS[ind.value]
                : null;
              return (
                <button key={ind.value}
                  onClick={() => setFilterType(ind.value)}
                  data-testid={`filter-type-${ind.value}`}
                  className="industry-pill-active flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold transition-all duration-200"
                  style={isActive ? {
                    background: tiers
                      ? `linear-gradient(135deg, ${tiers.low}, ${tiers.mid})`
                      : "rgba(46,204,113,0.15)",
                    color: ind.color,
                    border: `1.5px solid ${ind.color}`,
                    boxShadow: `0 0 20px ${ind.color}35, 0 0 40px ${ind.color}10`,
                    transform: "scale(1.05)"
                  } : {
                    background: `${ind.color}0c`,
                    color: ind.color === "#64748b" ? "#475569" : `${ind.color}99`,
                    border: `1px solid ${ind.color}22`
                  }}>
                  {IconComp && <IconComp size={13} style={{ color: isActive ? ind.color : undefined }} />}
                  <span>{ind.label}</span>
                  {isActive && (
                    <div className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ background: ind.color, animation: "livePulse 1.5s infinite" }} />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Map + Sidebar ────────────────────────────────────────────────────── */}
      <section className="py-8">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

            {/* Map Card */}
            <div className="lg:col-span-2 rounded-2xl p-5 relative overflow-hidden"
              style={{
                background: "rgba(10,15,26,0.95)",
                border: filterType !== "all" && activeIndustry
                  ? `1px solid ${activeIndustry.color}30`
                  : "1px solid rgba(255,255,255,0.08)",
                boxShadow: filterType !== "all" && activeIndustry
                  ? `0 8px 40px rgba(0,0,0,0.5), 0 0 40px ${activeIndustry.color}08`
                  : "0 8px 40px rgba(0,0,0,0.5)",
                transition: "border-color 0.4s ease, box-shadow 0.4s ease"
              }}>

              {/* Map header */}
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#2ECC71]"
                    style={{ animation: "livePulse 2s infinite" }} />
                  Real-Time Territory Map
                </span>
                <MapModeBadge filterType={filterType} />
              </div>

              {/* Colored glow overlay when industry selected */}
              {filterType !== "all" && activeIndustry && (
                <div className="absolute inset-0 pointer-events-none rounded-2xl"
                  style={{
                    background: `radial-gradient(ellipse 60% 40% at 50% 50%, ${activeIndustry.color}06 0%, transparent 70%)`,
                  }} />
              )}

              <ComposableMap projection="geoAlbersUsa" style={{ width: "100%", height: "auto" }} data-testid="us-map">
                <Geographies geography={GEO_URL}>
                  {({ geographies }) =>
                    geographies.map(geo => {
                      const fips = String(geo.id).padStart(2, "0");
                      const code = FIPS_TO_STATE[fips];
                      const count = stateCounts[code] || 0;
                      const isSelected = selectedState === code;
                      const isHovered = hoveredState === code;
                      const fill = getStateFill(count, isSelected, isHovered, filterType);
                      return (
                        <Geography key={geo.rsmKey} geography={geo}
                          fill={fill}
                          stroke="#060B14"
                          strokeWidth={0.8}
                          onClick={() => code && handleStateClick(code)}
                          onMouseEnter={(e) => {
                            setHoveredState(code);
                            const indColor = activeIndustry?.color || "#2ECC71";
                            setTooltip({
                              visible: true,
                              x: e.clientX,
                              y: e.clientY,
                              content: `${STATE_NAMES[code] || code}: ${count} claimed`,
                              color: indColor
                            });
                          }}
                          onMouseMove={(e) => setTooltip(t => ({ ...t, x: e.clientX, y: e.clientY }))}
                          onMouseLeave={() => { setHoveredState(null); setTooltip(t => ({ ...t, visible: false })); }}
                          style={{ default: { outline: "none", cursor: "pointer" }, hover: { outline: "none" }, pressed: { outline: "none" } }}
                        />
                      );
                    })
                  }
                </Geographies>
              </ComposableMap>

              {tooltip.visible && (
                <div className="fixed z-50 text-white text-xs px-3 py-1.5 rounded-lg pointer-events-none"
                  style={{
                    left: tooltip.x + 14, top: tooltip.y - 36,
                    background: "rgba(6,11,20,0.97)",
                    border: `1px solid ${tooltip.color}40`,
                    boxShadow: `0 4px 20px rgba(0,0,0,0.6), 0 0 8px ${tooltip.color}20`
                  }}>
                  {tooltip.content}
                </div>
              )}

              {/* Density legend — updates per industry */}
              <div className="flex items-center gap-4 mt-4 flex-wrap pt-3"
                style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                <span className="text-slate-500 text-xs font-bold uppercase tracking-wide">
                  {filterType !== "all" && activeIndustry ? `${activeIndustry.label} Density:` : "Density:"}
                </span>
                {legendItems.map(l => (
                  <div key={l.label} className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm flex-shrink-0"
                      style={{ background: l.color, border: "1px solid rgba(255,255,255,0.1)" }} />
                    <span className="text-slate-400 text-xs">{l.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Sidebar */}
            <div className="rounded-2xl p-5 transition-all duration-400" data-testid="map-sidebar"
              style={{
                background: "rgba(10,15,26,0.95)",
                border: selectedState ? `1px solid rgba(46,204,113,0.25)` : "1px solid rgba(255,255,255,0.08)",
                boxShadow: "0 8px 40px rgba(0,0,0,0.4)"
              }}>
              {!selectedState ? (
                <div className="flex flex-col h-full">
                  <div className="text-center py-4">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
                      style={{ background: "rgba(46,204,113,0.1)", border: "1px solid rgba(46,204,113,0.25)", boxShadow: "0 0 20px rgba(46,204,113,0.1)" }}>
                      <MapPin size={24} className="text-[#2ECC71]" />
                    </div>
                    <p className="text-white font-bold text-base">Click any state</p>
                    <p className="text-slate-400 text-sm mt-1">to explore available ZIP spots</p>
                  </div>

                  {topStates.length > 0 && (
                    <div className="mt-2 flex-1">
                      <div className="flex items-center gap-2 mb-3 px-1">
                        <Star size={12} className="text-[#FFD700]" />
                        <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Territory Leaderboard</span>
                      </div>
                      <div className="space-y-2">
                        {topStates.map(([s, c], i) => (
                          <button key={s} onClick={() => handleStateClick(s)}
                            className="state-leaderboard-row w-full flex items-center gap-3 px-3 py-2.5 rounded-xl group"
                            style={{
                              background: "rgba(8,12,20,0.8)",
                              border: "1px solid rgba(255,255,255,0.06)"
                            }}>
                            <RankMedal rank={i + 1} />
                            <span className="text-white text-sm font-semibold flex-1 text-left group-hover:text-[#2ECC71] transition-colors duration-150">
                              {STATE_NAMES[s] || s}
                            </span>
                            <div className="flex items-center gap-1">
                              <span className="font-black text-sm" style={{
                                background: "linear-gradient(135deg, #2ECC71, #06b6d4)",
                                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text"
                              }}>{c}</span>
                              <ChevronRight size={12} className="text-slate-600 group-hover:text-slate-400 transition-colors" />
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Mini industry color strip */}
                  <div className="mt-4 pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                    <p className="text-slate-600 text-xs font-bold uppercase tracking-widest mb-2">Industry Colors</p>
                    <div className="flex flex-wrap gap-1.5">
                      {INDUSTRY_TYPES.filter(t => t.value !== "all").map(ind => {
                        const IconComp = ind.icon;
                        return (
                          <button key={ind.value}
                            onClick={() => setFilterType(ind.value)}
                            title={ind.label}
                            className="w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                            style={{
                              background: `${ind.color}20`,
                              border: `1.5px solid ${ind.color}50`,
                              boxShadow: filterType === ind.value ? `0 0 10px ${ind.color}60` : "none"
                            }}>
                            {IconComp && <IconComp size={12} style={{ color: ind.color }} />}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <Link to="/claim" className="mt-4 flex items-center justify-center gap-2 w-full font-bold py-3 rounded-xl text-sm transition-all duration-200 hover:brightness-110"
                    style={{
                      background: "linear-gradient(135deg, rgba(46,204,113,0.15), rgba(6,182,212,0.15))",
                      border: "1px solid rgba(46,204,113,0.25)",
                      color: "#2ECC71"
                    }}>
                    <Zap size={14} /> Claim an Exclusive ZIP <ArrowRight size={13} />
                  </Link>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-xs text-slate-500 uppercase tracking-wide font-bold mb-0.5">Selected State</div>
                      <h2 className="text-xl font-black text-white">{STATE_NAMES[selectedState] || selectedState}</h2>
                    </div>
                    <button onClick={() => { setSelectedState(null); setFilterState("all"); }}
                      className="text-slate-400 hover:text-white p-1.5 rounded-lg transition-colors"
                      style={{ background: "rgba(255,255,255,0.06)" }}>
                      <X size={16} />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <div className="rounded-xl p-3 text-center"
                      style={{ background: "rgba(46,204,113,0.08)", border: "1px solid rgba(46,204,113,0.2)" }}>
                      <div className="text-2xl font-black" style={{
                        background: "linear-gradient(135deg, #2ECC71, #06b6d4)",
                        WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text"
                      }}>{stateCounts[selectedState] || 0}</div>
                      <div className="text-slate-400 text-xs mt-0.5">Claimed</div>
                    </div>
                    <div className="rounded-xl p-3 text-center"
                      style={{ background: "rgba(8,13,22,0.8)", border: "1px solid rgba(255,255,255,0.07)" }}>
                      <div className="text-2xl font-black" style={{
                        background: "linear-gradient(135deg, #f43f5e, #a78bfa, #38bdf8)",
                        WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text"
                      }}>{CLAIMABLE_INDUSTRY_TYPES.length}</div>
                      <div className="text-slate-400 text-xs mt-0.5">Industries</div>
                    </div>
                  </div>

                  <div className="space-y-1 mb-4 max-h-64 overflow-y-auto scrollbar-hide">
                    {CLAIMABLE_INDUSTRY_TYPES.map(ind => {
                      const IconComp = ind.icon;
                      const count = listings.filter(l => l.state === selectedState && (l.advertiser_type || "tax_pro") === ind.value).length;
                      return (
                        <div key={ind.value} className="flex items-center justify-between px-3 py-2 rounded-lg"
                          style={{ background: "rgba(8,12,20,0.7)", borderLeft: `2px solid ${count > 0 ? "#f8717120" : `${ind.color}15`}` }}>
                          <span className="flex items-center gap-2 text-sm font-medium text-slate-300">
                            {IconComp && <IconComp size={12} style={{ color: ind.color, flexShrink: 0 }} />}
                            {ind.label}
                          </span>
                          <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                            style={count > 0
                              ? { color: "#f87171", background: "rgba(248,113,113,0.12)", border: "1px solid rgba(248,113,113,0.2)" }
                              : { color: ind.color, background: `${ind.color}12`, border: `1px solid ${ind.color}20` }}>
                            {count > 0 ? `${count} taken` : "OPEN"}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <Link to="/claim"
                    className="flex items-center justify-center gap-2 w-full font-bold py-3 rounded-xl transition-all duration-200 hover:brightness-110 text-sm"
                    style={{
                      background: "linear-gradient(135deg, #2ECC71, #16a34a)",
                      color: "white",
                      boxShadow: "0 4px 20px rgba(46,204,113,0.3)"
                    }}>
                    Claim a ZIP in {selectedState} <ArrowRight size={15} />
                  </Link>
                </div>
              )}
            </div>

          </div>
        </div>
      </section>

      {/* ── Advertiser Marketplace ────────────────────────────────────────────── */}
      <section ref={marketplaceRef} className="py-14 scroll-mt-28"
        style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}
        data-testid="marketplace-section">
        <div className="section-container">

          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8">
            <div>
              <div className="inline-flex items-center gap-1.5 mb-2 text-xs font-bold uppercase tracking-widest"
                style={{ color: "#2ECC71" }}>
                <Activity size={11} /> Advertiser Marketplace
              </div>
              <h2 className="text-3xl font-black text-white">Exclusive ZIP Code Spots</h2>
              <p className="text-slate-400 mt-1.5 max-w-xl text-sm">
                {CLAIMABLE_INDUSTRY_TYPES.length} industries · one exclusive advertiser per ZIP per industry · $150/year.
              </p>
            </div>
            <Link to="/claim"
              className="flex items-center gap-2 font-bold px-6 py-3 rounded-xl text-sm whitespace-nowrap transition-all duration-200 hover:scale-105"
              style={{
                background: "linear-gradient(135deg, #2ECC71, #16a34a)",
                color: "white",
                boxShadow: "0 4px 24px rgba(46,204,113,0.3)"
              }}>
              Claim a Spot <ArrowRight size={15} />
            </Link>
          </div>

          {filterState !== "all" && (
            <div className="flex items-center gap-2 mb-5">
              <span className="text-slate-400 text-sm">Filtering:</span>
              <span className="inline-flex items-center gap-2 text-sm font-bold px-3 py-1 rounded-full"
                style={{ background: "rgba(46,204,113,0.12)", color: "#2ECC71", border: "1px solid rgba(46,204,113,0.25)" }}>
                <MapPin size={12} /> {STATE_NAMES[filterState] || filterState}
                <button onClick={() => { setFilterState("all"); setSelectedState(null); }}
                  className="hover:text-white transition-colors"><X size={12} /></button>
              </span>
            </div>
          )}

          {banners.length > 0 && (
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Star size={14} className="text-[#FFD700]" />
                <h3 className="text-white font-bold text-lg">Featured Advertisers</h3>
                {banners.length > 1 && (
                  <span className="text-slate-500 text-sm">· {banners.length} ads · auto-rotates every 8s</span>
                )}
              </div>
              <div className="max-w-2xl">
                <RotatingBanners banners={banners} onCTAClick={trackAndOpen} trackImpression={true} />
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center py-16">
              <div className="w-8 h-8 border-2 border-[#2ECC71] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-slate-400 text-sm">Loading marketplace data...</p>
            </div>
          ) : listings.length > 0 ? (
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Shield size={14} className="text-slate-400" />
                <h3 className="text-white font-bold">Claimed Spots ({listings.length})</h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {listings.map(l => <ClaimedCard key={l.id} listing={l} />)}
              </div>
            </div>
          ) : (
            <div className="text-center py-14 rounded-2xl mb-8"
              style={{ border: "1px dashed rgba(255,255,255,0.08)", background: "rgba(8,12,20,0.5)" }}>
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ background: "rgba(46,204,113,0.08)", border: "1px solid rgba(46,204,113,0.15)" }}>
                <MapPin size={24} className="text-slate-500" />
              </div>
              <p className="text-slate-300 font-bold text-lg">
                No claimed spots {filterState !== "all" ? `in ${STATE_NAMES[filterState] || filterState}` : "yet"}
              </p>
              <p className="text-slate-500 text-sm mt-2">Be the first to claim an exclusive territory here!</p>
              <Link to="/claim"
                className="inline-flex items-center gap-2 mt-5 font-bold px-6 py-3 rounded-xl text-sm transition-all duration-200 hover:scale-105"
                style={{
                  background: "linear-gradient(135deg, #2ECC71, #16a34a)",
                  color: "white",
                  boxShadow: "0 4px 20px rgba(46,204,113,0.3)"
                }}>
                Claim a ZIP Code <ArrowRight size={15} />
              </Link>
            </div>
          )}

          {/* CTA Banner */}
          <div className="rounded-2xl p-8 text-center relative overflow-hidden"
            style={{
              background: "rgba(8,12,20,0.98)",
              border: "1px solid rgba(46,204,113,0.15)",
              boxShadow: "0 0 60px rgba(46,204,113,0.05)"
            }}>
            <div className="absolute inset-0 map-intel-grid opacity-40 pointer-events-none" />
            {/* Rainbow gradient top bar */}
            <div className="absolute top-0 left-0 right-0 h-px"
              style={{ background: "linear-gradient(90deg, #f43f5e, #f97316, #facc15, #2ECC71, #06b6d4, #6366f1, #e879f9)" }} />
            <div className="relative">
              <div className="inline-flex items-center gap-1.5 mb-3 text-xs font-bold uppercase tracking-widest"
                style={{ color: "#2ECC71" }}>
                <div className="w-1.5 h-1.5 rounded-full bg-[#2ECC71]" style={{ animation: "livePulse 2s infinite" }} />
                Open Territory
              </div>
              <h3 className="text-2xl font-black text-white mb-2">Your ZIP Code May Still Be Available</h3>
              <p className="text-slate-400 max-w-md mx-auto mb-6 text-sm">
                {CLAIMABLE_INDUSTRY_TYPES.length} industries · exclusive per ZIP · $150/year. Your banner shown to every local visitor. Clicks tracked live.
              </p>
              <div className="flex flex-wrap justify-center gap-2 mb-6">
                {CLAIMABLE_INDUSTRY_TYPES.map(ind => {
                  const IconComp = ind.icon;
                  return (
                    <Link key={ind.value} to={`/claim?type=${ind.value}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 hover:scale-105"
                      style={{ color: ind.color, background: `${ind.color}12`, border: `1px solid ${ind.color}35` }}>
                      <IconComp size={11} /> {ind.label}
                    </Link>
                  );
                })}
              </div>
              <Link to="/claim"
                className="inline-flex items-center gap-2 font-black px-8 py-4 rounded-xl text-base transition-all duration-200 hover:scale-105 hover:brightness-110"
                style={{
                  background: "linear-gradient(135deg, #2ECC71, #16a34a)",
                  color: "white",
                  boxShadow: "0 6px 30px rgba(46,204,113,0.4)"
                }}>
                Claim Your ZIP Code — $150 <ArrowRight size={17} />
              </Link>
            </div>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
}
